import type { MediaLinkContext } from "../upload/mediaLinkContext"
import type { JSONContent } from "@tiptap/core"
import { AiUsageLevel, ArticleKind, ArticleRelation, CarouselPlacement, PublishedState } from "../graphql/types"
import type {
  ArticleDetailsFragment,
  ArticleRatingCategoryInput,
  ArticleRatingInput,
  AuthorListItemFragment,
  CompanyListItemFragment,
  CreateArticleMutationVariables,
  GameConsoleListItemFragment,
  GameListItemFragment,
  GameSeriesListItemFragment,
  MediaListItemFragment,
  RatingCategoryDetailsFragment,
  RatingCategoryListItemFragment,
  SubReviewInput,
  TagListItemFragment,
  UpdateArticleMutationVariables,
} from "../graphql/types"
import { DateTime } from "luxon"
import { TZ_EUROPE_BERLIN } from "../timezone"
import type { StringValue } from "../values"
import type { OrderRaw } from "../components/order"

export const ARTICLE_AUTHORS_STORAGE_KEY = "nfans.articles.authors"
export const ARTICLE_TAGS_STORAGE_KEY = "nfans.articles.tags"
export const ARTICLE_COMPANIES_STORAGE_KEY = "nfans.articles.companies"
export const ARTICLE_GAME_CONSOLES_STORAGE_KEY = "nfans.articles.gameConsoles"
export const ARTICLE_GAME_SERIES_STORAGE_KEY = "nfans.articles.gameSeries"
export const ARTICLE_GAMES_STORAGE_KEY = "nfans.articles.games"
export const ARTICLE_KINDS_STORAGE_KEY = "nfans.articles.kinds"
export const ARTICLE_PUBLISHED_STATES_STORAGE_KEY = "nfans.articles.publishedStates"
export const ARTICLE_CAROUSEL_PLACEMENTS_STORAGE_KEY = "nfans.articles.carouselPlacements"
export const ARTICLE_PUBLICATION_YEAR_STORAGE_KEY = "nfans.articles.publicationYear"
export const ARTICLE_PUBLICATION_MONTH_STORAGE_KEY = "nfans.articles.publicationMonth"
export const ARTICLE_ORDER_STORAGE_KEY = "nfans.articles.order"
export const ARTICLE_WITHOUT_STORAGE_KEY = "nfans.articles.without"
export const ARTICLE_WITH_ANY_STORAGE_KEY = "nfans.articles.withAny"

export const ARTICLE_ORDER_PROPERTIES: Array<OrderRaw> = [
  { label: "Veröffentlicht",   properties: [{ property: "publicationDateTime", defaultDirection: "desc" }] },
  { label: "Zuletzt geändert", properties: [{ property: "lastModifiedDate",    defaultDirection: "desc" }] },
  { label: "Erstellt",         properties: [{ property: "createdDate",         defaultDirection: "desc" }] },
  { label: "Titel",            properties: [{ property: "subject",             defaultDirection: "asc"  }] },
  { label: "Bewertung",        properties: [{ property: "totalScore",          defaultDirection: "desc" }] },
  { label: "Artikeltyp",       properties: [{ property: "kind",                defaultDirection: "asc"  }] },
  { label: "Reaktionen",       properties: [{ property: "totalReactions",      defaultDirection: "desc" }] },
  { label: "Kommentare",       properties: [{ property: "commentCount",        defaultDirection: "desc" }] },
]

/**
 * A ProseMirror / TipTap rich-text field (article body, teaser, conclusion, …).
 * It flows through a GraphQL `JSON` scalar, so at the boundaries it can be a
 * TipTap `JSONContent` doc, a serialized JSON string, or null (empty/optional
 * Fazit) — a real union the previous file-wide `any` was hiding. Still far
 * stricter than `any` (rejects numbers, arrays, etc.).
 */
type ProseMirrorDoc = JSONContent | string | null

// Factory (not a shared constant): each form field needs its OWN empty doc — the
// TipTap editor mutates these in place, so sharing one object would bleed edits
// across fields.
function emptyTiptapDoc() {
  return { type: "doc", content: [{ type: "paragraph" }] }
}

export interface SubReviewItem {
  /** Client-only stable key for list rendering & drag-reorder (sub-reviews have no server id). */
  _key: string
  title: string
  body: ProseMirrorDoc
  conclusion: ProseMirrorDoc // optional per-product Fazit
  teaserMedia: MediaListItemFragment | null | undefined
  pros: StringValue[]
  cons: StringValue[]
  ratingCategories: RatingCategoryDetailsFragment[]
  ratingScores: Record<string, number>
  score: number | null
}

type TestOnlyFields = Pick<
  ArticleFormData,
  | "conclusion" | "editorsTake" | "accessibilityNote" | "specialThanks"
  | "pros" | "cons" | "rating" | "ratingNA"
  | "totalScore" | "overwriteTotalScore" | "highlyAccessible"
  | "subReviews"
>

function testOnlyFieldsIfKind(data: ArticleFormData): TestOnlyFields {
  if (data.kind === ArticleKind.TEST) {
    return {
      conclusion: data.conclusion,
      editorsTake: data.editorsTake,
      accessibilityNote: data.accessibilityNote,
      specialThanks: data.specialThanks,
      pros: data.pros,
      cons: data.cons,
      rating: data.rating,
      ratingNA: data.ratingNA,
      totalScore: data.totalScore,
      overwriteTotalScore: data.overwriteTotalScore,
      highlyAccessible: data.highlyAccessible,
      subReviews: data.subReviews,
    }
  }
  return {
    conclusion: emptyTiptapDoc(),
    editorsTake: emptyTiptapDoc(),
    accessibilityNote: emptyTiptapDoc(),
    specialThanks: emptyTiptapDoc(),
    pros: [],
    cons: [],
    rating: {},
    ratingNA: {},
    totalScore: 0,
    overwriteTotalScore: false,
    highlyAccessible: false,
    subReviews: [],
  }
}

export interface ArticleFormData {
  uuid: string
  kind: ArticleKind
  subject: string
  authors: AuthorListItemFragment[]
  teaserMedia: MediaListItemFragment | null | undefined
  teaserText: ProseMirrorDoc
  text: ProseMirrorDoc
  conclusion: ProseMirrorDoc
  editorsTake: ProseMirrorDoc
  highlyAccessible: boolean
  accessibilityNote: ProseMirrorDoc
  specialThanks: ProseMirrorDoc
  pros: StringValue[]
  cons: StringValue[]
  rating: Record<string, number>
  ratingNA: Record<string, boolean>
  overwriteTotalScore: boolean
  totalScore: number
  externalMediaUrls: StringValue[]
  medias: MediaListItemFragment[]
  games: GameListItemFragment[]
  gameSeries: GameSeriesListItemFragment[]
  gameConsoles: GameConsoleListItemFragment[]
  companies: CompanyListItemFragment[]
  tags: TagListItemFragment[]
  articleSeries: string | null
  event: string | null
  publishedState: PublishedState
  aiUsageLevel: AiUsageLevel
  carouselPlacement: CarouselPlacement
  publicationDate: string
  publicationTime: string
  publicationTimezone: string
  seoDescription: string
  subReviews: SubReviewItem[]
}

export function articleMediaLinkContext(formData: ArticleFormData): MediaLinkContext {
  return {
    games: formData.games.map((g) => g.uuid),
    gameSeries: formData.gameSeries.map((s) => s.uuid),
    gameConsoles: formData.gameConsoles.map((c) => c.uuid),
    companies: formData.companies.map((c) => c.uuid),
    tags: formData.tags.map((t) => t.uuid),
  }
}

export function createEmptyArticleFormData(ratingCategories: RatingCategoryDetailsFragment[]): ArticleFormData {
  return {
    uuid: "",
    kind: ArticleKind.TEST,
    subject: "",
    authors: [],
    teaserMedia: null,
    teaserText: emptyTiptapDoc(),
    text: emptyTiptapDoc(),
    conclusion: emptyTiptapDoc(),
    editorsTake: emptyTiptapDoc(),
    highlyAccessible: false,
    accessibilityNote: emptyTiptapDoc(),
    specialThanks: emptyTiptapDoc(),
    pros: [{ value: '' }, { value: '' }, { value: '' }],
    cons: [{ value: '' }, { value: '' }, { value: '' }],
    rating: Object.fromEntries(ratingCategories.map(category => [category.uuid, 1])),
    ratingNA: Object.fromEntries(ratingCategories.map(category => [category.uuid, !category.required])),
    overwriteTotalScore: false,
    totalScore: 0,
    externalMediaUrls: [],
    medias: [],
    games: [],
    gameSeries: [],
    gameConsoles: [],
    companies: [],
    tags: [],
    articleSeries: null,
    event: null,
    publishedState: PublishedState.DRAFT,
    aiUsageLevel: AiUsageLevel.NONE,
    carouselPlacement: CarouselPlacement.STANDARD,
    publicationDate: "",
    publicationTime: "",
    publicationTimezone: TZ_EUROPE_BERLIN,
    seoDescription: '',
    subReviews: [],
  }
}

export interface ExternalAuthorFormData {
  name: string
  email: string
  url: string
}

export function toArticleFormData(
  article: ArticleDetailsFragment,
  ratingCategories: Array<RatingCategoryListItemFragment>,
): ArticleFormData {
  const uuid: string = article.uuid
  const kind: ArticleKind = article.kind ?? ArticleKind.TEST
  const subject: string = article.subject
  const authors = article.authors
  const teaserMedia: MediaListItemFragment | null | undefined = article.teaserMedia
  const teaserText: string = article.teaserText
  const text: string = article.text
  const conclusion: string = article.conclusion
  const editorsTake: string = article.editorsTake
  const highlyAccessible: boolean = article.highlyAccessible
  const accessibilityNote: string = article.accessibilityNote
  const specialThanks: string = article.specialThanks
  const pros: StringValue[] = article.pros.map(value => ({ value }))
  const cons: StringValue[] = article.cons.map(value => ({ value }))
  // A null score means the category is attached but not yet scored ("N.A.").
  const rating = Object.fromEntries(article.rating.map(item => ([item.category.uuid, item.score ?? item.category.min])))
  const ratingNA = Object.fromEntries(ratingCategories.map(category => {
    const item = article.rating.find(item => category.uuid === item.category.uuid)
    return [category.uuid, !item || item.score == null]
  }))
  const totalScore = article.totalScore
  const externalMediaUrls: StringValue[] = article.medias
    .filter((m): m is { __typename: 'ExternalMedia'; url: string } => m.__typename === 'ExternalMedia')
    .map(m => ({ value: m.url }))
  const medias: MediaListItemFragment[] = article.medias.filter(
    (m): m is MediaListItemFragment => m.__typename === 'Media'
  )
  const games = article.games
  const gameSeries = article.gameSeries
  const gameConsoles = article.gameConsoles
  const companies = article.companies
  const tags = article.tags
  const articleSeries = article.articleSeries?.uuid ?? null
  const event = article.event?.uuid ?? null
  const publishedState: PublishedState = article.publishedState
  const aiUsageLevel: AiUsageLevel = article.aiUsageLevel ?? AiUsageLevel.NONE
  const carouselPlacement: CarouselPlacement = article.carouselPlacement ?? CarouselPlacement.STANDARD
  const publicationDateTime = article.publicationDateTime ? DateTime.fromISO(article.publicationDateTime) : undefined
  const publicationDate = (publicationDateTime && publicationDateTime.toISODate()) ?? ""
  const publicationTime = publicationDateTime?.toFormat("HH:mm") ?? ""
  const publicationTimezone = publicationDateTime?.zoneName ?? TZ_EUROPE_BERLIN
  const seoDescription = article.seoDescription ?? ''
  // Schluessel aus der Position, nicht zufaellig: der beim Speichern neu geladene Stand muss dem
  // beim Oeffnen gleichen, sonst meldet der Konfliktschutz Schein-Aenderungen an subReviews.
  const subReviews: SubReviewItem[] = (article.subReviews ?? []).map((s, index) => ({
    _key: `sr-${index}`,
    title: s.title,
    body: s.body,
    conclusion: s.conclusion ?? null,
    teaserMedia: s.teaserMedia ?? null,
    pros: s.pros.map((value) => ({ value })),
    cons: s.cons.map((value) => ({ value })),
    ratingCategories: s.rating.map((r) => r.category),
    ratingScores: Object.fromEntries(s.rating.filter((r) => r.score != null).map((r) => [r.category.uuid, r.score as number])),
    score: s.score ?? null,
  }))

  return {
    uuid,
    kind,
    subject,
    authors,
    teaserMedia,
    teaserText,
    text,
    conclusion,
    editorsTake,
    highlyAccessible,
    accessibilityNote,
    specialThanks,
    pros,
    cons,
    rating,
    ratingNA,
    // legacy documents carry no flag: treat a stored score as manual, as before
    overwriteTotalScore: article.manualTotalScore ?? !!totalScore,
    totalScore: totalScore ?? 0,
    externalMediaUrls,
    medias,
    games,
    gameSeries,
    gameConsoles,
    companies,
    tags,
    articleSeries,
    event,
    publishedState,
    aiUsageLevel,
    carouselPlacement,
    publicationDate,
    publicationTime,
    publicationTimezone,
    seoDescription,
    subReviews,
  }
}

// Shared payload builder for create + update — the two mutation inputs have the
// same shape, so both wrappers below reuse this single mapper.
function toArticleInput(article: ArticleFormData, ratingCategories: RatingCategoryDetailsFragment[]) {
  const tests = testOnlyFieldsIfKind(article)

  const kind = article.kind
  const subject = article.subject
  const authors = article.authors.map(a => a.uuid)
  const teaserMedia = article.teaserMedia?.uuid
  const teaserText = article.teaserText
  const text = article.text
  const conclusion = tests.conclusion
  const editorsTake = tests.editorsTake
  const highlyAccessible = tests.highlyAccessible
  const accessibilityNote = tests.accessibilityNote
  const specialThanks = tests.specialThanks
  const pros = tests.pros.map(value => value.value)
  const cons = tests.cons.map(value => value.value)
  const rating = toArticleRatingInputs(tests.rating, tests.ratingNA, ratingCategories)
  const totalScore = tests.totalScore
  const medias = [
    ...article.externalMediaUrls.filter(u => u.value.trim() !== '').map(u => ({ url: u.value })),
    ...article.medias.map(item => ({ uuid: item.uuid })),
  ]
  const games = article.games.map(g => g.uuid)
  const gameSeries = article.gameSeries.map(g => g.uuid)
  const gameConsoles = article.gameConsoles.map(g => g.uuid)
  const companies = article.companies.map(c => c.uuid)
  const tags = article.tags.map(t => t.uuid)
  const articleSeries = article.articleSeries ?? null
  const event = article.event ?? null
  const publishedState = article.publishedState
  const aiUsageLevel = article.aiUsageLevel
  const carouselPlacement = article.carouselPlacement
  const publicationDateTime = toDateTime(article.publicationDate, article.publicationTime, article.publicationTimezone)

  return {
    kind,
    subject,
    authors,
    teaserMedia,
    teaserText,
    text,
    conclusion,
    editorsTake,
    highlyAccessible,
    accessibilityNote,
    specialThanks,
    pros,
    cons,
    rating,
    totalScore,
    manualTotalScore: tests.overwriteTotalScore,
    subReviews: toSubReviewInputs(tests.subReviews),
    medias,
    games,
    gameSeries,
    gameConsoles,
    companies,
    tags,
    articleSeries,
    event,
    publishedState,
    aiUsageLevel,
    carouselPlacement,
    publicationDateTime,
    seoDescription: article.seoDescription || undefined,
  }
}

export function toCreateInput(article: ArticleFormData, ratingCategories: RatingCategoryDetailsFragment[]): CreateArticleMutationVariables {
  return { create: toArticleInput(article, ratingCategories) }
}

export function toUpdateInput(article: ArticleFormData, ratingCategories: RatingCategoryDetailsFragment[]): UpdateArticleMutationVariables {
  return { id: article.uuid, update: toArticleInput(article, ratingCategories) }
}

export function toArticleRatingInputs(
  rating: Record<string, number>,
  ratingNA: Record<string, boolean>,
  ratingCategories: RatingCategoryDetailsFragment[],
): ArticleRatingInput[] {
  const result: ArticleRatingInput[] = []
  ratingCategories.forEach(category => {
    // Only categories the form actually tracks belong to this article. For
    // non-TEST kinds both maps are empty, so nothing is emitted.
    const known = category.uuid in rating || category.uuid in ratingNA
    if (!known) {
      return
    }

    // N.A. categories are preserved with a null score (attached but not yet
    // scored) instead of being dropped, so an intermediate save keeps them.
    const score = ratingNA[category.uuid] ? null : rating[category.uuid] ?? null
    result.push(toArticleRatingInput(category, score))
  })
  return result
}

function toArticleRatingInput(category: RatingCategoryDetailsFragment, score: number | null): ArticleRatingInput {
  return {
    category: toArticleRatingCategoryInput(category),
    score,
  }
}

export function toSubReviewInputs(items: SubReviewItem[]): SubReviewInput[] {
  return items.map((s) => ({
    title: s.title,
    body: s.body,
    conclusion: s.conclusion,
    teaserMedia: s.teaserMedia?.uuid,
    pros: s.pros.map((p) => p.value),
    cons: s.cons.map((c) => c.value),
    rating: s.ratingCategories.map((cat) => ({
      category: toArticleRatingCategoryInput(cat),
      score: s.ratingScores[cat.uuid] ?? null,
    })),
    score: s.score,
  }))
}

export function isAdhoc(category: RatingCategoryDetailsFragment): boolean {
  return category.articleSpecific === true
}

export function nextAdhocOrder(categories: RatingCategoryDetailsFragment[]): number {
  return categories.reduce((max, c) => Math.max(max, c.order), 0) + 1
}

export function createAdhocRatingCategory(order: number): RatingCategoryDetailsFragment {
  return {
    uuid: crypto.randomUUID(),
    name: "",
    min: 1,
    max: 10,
    step: 0.5,
    required: false,
    order,
    articleSpecific: true,
  } as RatingCategoryDetailsFragment
}

// Renumber ad-hoc criteria sequentially so their order stays dense and always
// sits after the system block. Preserves the given list order.
export function renumberAdhocOrders(
  adhoc: RatingCategoryDetailsFragment[],
  systemMaxOrder: number,
): RatingCategoryDetailsFragment[] {
  return adhoc.map((c, i) => ({ ...c, order: systemMaxOrder + i + 1 }))
}

// Live preview of the server's auto-total formula (`autoTotalScore` in
// ArticleRatingEmbeddable.kt): a criterion counts if required OR not N.A. and its score
// is > 0, each score normalized to /10 so mixed scales combine. The server recomputes and
// persists the number on save whenever the score is not set by hand — this copy exists
// only so the editor sees the result while moving sliders. Keep both tests on one fixture.
export function computeAutoTotalScore(
  categories: RatingCategoryDetailsFragment[],
  rating: Record<string, number>,
  ratingNA: Record<string, boolean>,
): number {
  const contributions = categories
    .map(cat =>
      (cat.required || !ratingNA[cat.uuid])
        ? ((rating[cat.uuid] ?? 0) / cat.max) * 10
        : 0,
    )
    .filter(score => score > 0)
  return contributions.length > 0
    ? contributions.reduce((a, b) => a + b, 0) / contributions.length
    : 0
}

export function toArticleRatingCategoryInput(category: RatingCategoryDetailsFragment): ArticleRatingCategoryInput {
  return {
    uuid: category.uuid,
    name: category.name,
    min: category.min,
    max: category.max,
    step: category.step,
    required: category.required,
    order: category.order,
    articleSpecific: category.articleSpecific,
  }
}

/**
 * URL-Token → GraphQL-Enum. Die Schlüssel sind bewusst die BESTEHENDEN Filternamen aus
 * ArticleListView, damit die URL zu den übrigen Parametern passt — daher `events` (Plural)
 * gegenüber dem Singular-Enum EVENT. Die Tabelle bedient beide Richtungen: `?without=` (fehlt)
 * und `?withAny=` (irgendeine Zuweisung).
 */
export const ARTICLE_RELATION_TOKENS = {
  authors: ArticleRelation.AUTHORS,
  tags: ArticleRelation.TAGS,
  companies: ArticleRelation.COMPANIES,
  gameConsoles: ArticleRelation.GAME_CONSOLES,
  gameSeries: ArticleRelation.GAME_SERIES,
  games: ArticleRelation.GAMES,
  articleSeries: ArticleRelation.ARTICLE_SERIES,
  events: ArticleRelation.EVENT,
} as const

export type ArticleRelationName = keyof typeof ARTICLE_RELATION_TOKENS

const ARTICLE_RELATION_NAMES = new Set<string>(Object.keys(ARTICLE_RELATION_TOKENS))

export function parseRelationNames(raw: string[]): ArticleRelationName[] {
  const valid = raw.filter((v): v is ArticleRelationName => ARTICLE_RELATION_NAMES.has(v))
  return [...new Set(valid)]
}

export function toArticleRelations(names: ArticleRelationName[]): ArticleRelation[] {
  return names.map((name) => ARTICLE_RELATION_TOKENS[name])
}

export function toDateTime(date?: string | null, time?: string | null, zone?: string | null): string | undefined {
  if (date) {
    return DateTime
        .fromISO(time ? `${date}T${time}` : date, {zone: zone ?? TZ_EUROPE_BERLIN})
        .toISO()
      ?? undefined
  } else {
    return undefined
  }
}
